/**
 * Portable Party Games — 50 componentless games, each a rules page plus
 * per-day saved notes. This is the app-native catalog; the source text
 * is the printed book ("50 games from around the world, play anywhere,
 * anytime, only rules").
 *
 * TestFlight preview state: only game 6 (SARDINES) is filled in. Every
 * other slot is a numbered COMING SOON placeholder — `title: null`
 * marks a slot with no content yet.
 */

export interface PpgRuleSection {
  heading: string
  body: string
}

export interface PpgVariation {
  name: string
  body: string
}

/** The book's footer strip: ORIGIN · BRITAIN // PLAYERS · 4+ // … */
export interface PpgMeta {
  origin: string
  players: string
  time: string
  ages: string
}

export interface PpgGame {
  /** 1-based slot in the book, shown as a two-digit badge (06). */
  number: number
  /** null = COMING SOON placeholder row. */
  title: string | null
  sections?: PpgRuleSection[]
  variations?: PpgVariation[]
  meta?: PpgMeta
  /**
   * Jordan Draper original. JDG designs render in the brand yellow
   * (lime) instead of the PPG green — title, headings, chips, and
   * watermark — and their meta.origin reads "Jordan Draper" rather
   * than a country. Jordan flags which games get this as they're
   * imported.
   */
  jdg?: boolean
}

/** Namespaced key for user_game_notes rows ('ppg:6'). */
export function ppgNoteKey(gameNumber: number): string {
  return `ppg:${gameNumber}`
}

const CATEGORIES: PpgGame = {
  number: 1,
  title: 'Categories',
  meta: { origin: 'Unknown', players: '2+', time: '5 min', ages: '4+' },
  sections: [
    {
      heading: 'Form the Circle and Choose a Category',
      body: 'Players sit in a circle. One player (or the group) picks a category (e.g., animals, foods, countries, movies, superheroes, brands, colors, sports, or creative ones like "things that are gross" or "Disney characters"). The category should be broad enough for many answers.',
    },
    {
      heading: 'Choose the Starting Letter',
      body: 'Decide on the starting letter: often A to go through the full alphabet, or roll a die/randomly pick one. Some versions start with any letter and go sequentially from there.',
    },
    {
      heading: 'Go Around the Circle Alphabetically',
      body: "Starting with one player (clockwise or random order), each player in turn must name an item in the chosen category that starts with the next letter of the alphabet.\nPlayer 1: Something starting with A (e.g., for animals: Alligator).\nPlayer 2: B (e.g., Bear).\nPlayer 3: C (e.g., Cat).\nAnd so on, until Z.\nPlayers must respond quickly: hesitation (usually more than 3–5 seconds), repeats, or invalid answers mean they're \"out\" for that round (or the game continues without them).",
    },
    {
      heading: 'Handle Challenges and Penalties',
      body: "If a player can't think of an answer, they lose a life (or sit out). Others can challenge if an answer seems invalid (group vote decides).\nContinue until the alphabet is complete or only one player remains.",
    },
    {
      heading: 'End the Round and Rotate',
      body: 'The last player standing (or the one with most answers) "wins" the round. Pick a new category and start again. Some versions eliminate players and continue with survivors for multiple rounds.',
    },
  ],
}

const GHOST: PpgGame = {
  number: 9,
  title: 'Ghost',
  meta: { origin: 'USA', players: '2+', time: '10 min', ages: '8+' },
  sections: [
    {
      heading: 'Start the Game',
      body: 'The first player says any single letter (e.g., "C"). This begins the growing word string.',
    },
    {
      heading: 'Add One Letter Each Turn',
      body: 'The next player adds one letter to either end of the current string (in standard Ghost) or anywhere (in Superghost). The new string must still be the start of at least one real English word of three or more letters.\nPlayer 1: "C"\nPlayer 2: "CA"\nPlayer 3: "CAT" → illegal (completes the word "cat").',
    },
    {
      heading: 'Challenge a Player',
      body: 'At any time after a letter is added, the next player may challenge: "I challenge you!"\nThe challenged player must name a real word that begins with the current string.\nIf they can name a valid word → the challenger earns a penalty letter (G, then H, etc.).\nIf they cannot → the challenged player earns the penalty letter.',
    },
    {
      heading: 'Penalty Letters and Elimination',
      body: 'Each mistake (completing a word or failing a challenge) gives the player the next letter in "GHOST".\nOnce a player has all five letters (G-H-O-S-T), they are out or lose the round. The last player remaining (or the one who never spells GHOST) wins.',
    },
    {
      heading: 'End and Restart',
      body: 'After someone spells GHOST, start a new round. Players who are out can still participate by suggesting words or keeping score. Play as many rounds as you like.',
    },
  ],
  variations: [
    { name: 'Superghost', body: 'Players may add a letter to either end or in the middle of the string. Much more strategic!' },
  ],
}

const STREGA_COMANDA_COLORE: PpgGame = {
  number: 2,
  title: 'Strega Comanda Colore',
  meta: { origin: 'Italy', players: '3+', time: '5 min', ages: '4+' },
  sections: [
    {
      heading: 'Choose the First "Strega" (Witch/"It")',
      body: 'Use a counting rhyme (conta), rock-paper-scissors, or volunteer to select the "strega." Others scatter freely in the play area.',
    },
    {
      heading: 'Prompt the Chant (Optional Build-Up)',
      body: 'Strega shouts: "Strega comanda colore!" ("Witch commands color!")\nPlayers respond in chorus: "Che colore?" ("What color?")\nStrega names a color, e.g., "...verde!" ("...green!").',
    },
    {
      heading: 'Race to Safety',
      body: 'All players dash to touch any object (or clothing on another player, not their own) matching the color. They must touch and often shout the color (e.g., "Verde!") to be safe. Strega chases and tries to tag players before they touch.',
    },
    {
      heading: 'Tag and Rotate',
      body: 'Tagged player (before touching) becomes new strega.\nIf all safe, strega calls another color and chase repeats (no rotation).',
    },
    {
      heading: 'Repeat Indefinitely',
      body: 'Continues with new strega until exhaustion or agreement to stop. No overall winner. Pure fun!',
    },
  ],
  variations: [
    { name: 'Indiretta (Indirect)', body: 'Strega gives riddles, e.g., "Strega comanda il colore del cielo!" (blue).' },
    { name: 'Lettera (Letter)', body: 'Calls letters; touch objects starting with it.' },
    { name: 'Indoor/Written', body: 'List color-associated items on paper for points.' },
    { name: 'Multiple Colors', body: 'Calls combos (harder!).' },
  ],
}

const HINKY_PINKY: PpgGame = {
  number: 7,
  title: 'Hinky Pinky',
  meta: { origin: 'Britain', players: '3+', time: '15 min', ages: '7+' },
  sections: [
    {
      heading: 'Choose the First Riddle Master',
      body: 'Players decide who starts as the Riddle Master (the clue-giver) by volunteer, rock-paper-scissors, or counting out.',
    },
    {
      heading: 'Create the Rhyming Pair and Clue',
      body: 'The Master privately thinks of two words that rhyme (e.g., "loud hound"). They create a short descriptive clue using synonyms or definitions (e.g., "noisy canine"). They also announce the syllable level:\n"Stink Pink" = one syllable each\n"Stinky Pinky" = two syllables each\n"Stinkity Pinkity" = three syllables each',
    },
    {
      heading: 'Give the Clue',
      body: 'The Master announces the clue to the group, for example: "Stinky Pinky: noisy canine." Everyone thinks and calls out guesses.',
    },
    {
      heading: 'Guess the Answer',
      body: 'Players shout possible rhyming pairs. The Master confirms only when someone says the exact correct pair (e.g., "loud hound!"). Discussion and hints are usually allowed if the group is stuck.',
    },
    {
      heading: 'Rotate the Master',
      body: 'The first player to guess correctly becomes the new Riddle Master and creates the next Stinky Pinky. Play continues for as many rounds as desired.',
    },
  ],
  variations: [
    { name: 'Themed Rounds', body: 'Only animals, foods, or movie titles.' },
    { name: 'Strict No Hints', body: 'Master stays completely silent until someone guesses correctly.' },
    { name: 'Longer Chains', body: 'Create three-word rhymes (Stinkity Pinkity Pinkity).' },
  ],
}

const SARDINES: PpgGame = {
  number: 6,
  title: 'Sardines',
  meta: { origin: 'Britain', players: '4+', time: '15 min', ages: '6+' },
  sections: [
    {
      heading: 'Reverse the Hunt',
      body: 'Hide-and-seek, flipped inside out. ONE player hides while everyone else counts together (to fifty). Then the searchers scatter, each hunting alone.',
    },
    {
      heading: 'Find and Say Nothing',
      body: "When a searcher finds the hider, they don't announce it. They glance around, wait until no one's watching… and silently squeeze into the hiding spot too.",
    },
    {
      heading: 'Pack the Tin',
      body: 'One by one, searchers discover an increasingly ridiculous pile of humans behind the sofa, in the closet, under the stairs, and join it. Staying silent while packed like sardines is the real game; suppressed giggling has ended child-ran regimes.',
    },
    {
      heading: 'Lose by Wandering',
      body: 'The last searcher still roaming the building loses (gentle mockery is traditional). The first player to have found the hider becomes the next one to hide.',
    },
    {
      heading: 'Pick Fair Ground',
      body: 'Agree on boundaries before you start: which rooms, floors, or garden zones are in play, and anywhere genuinely unsafe is out.',
    },
  ],
  variations: [
    {
      name: 'Lights Out',
      body: 'Play at dusk or with lights dimmed. Atmospheric and much harder.',
    },
    {
      name: 'Plain Sight',
      body: 'In a park or lobby, the hider must "hide" somewhere visible but unremarkable.',
    },
    {
      name: 'Time Cap',
      body: 'Searchers still lost after ten minutes are called in by a group shout.',
    },
  ],
}

/**
 * Filled slots. Numbers come from the printed book itself (one game
 * per page: 001 CATEGORIES, 002 STREGA COMANDA COLORE, 007 HINKY
 * PINKY, 009 GHOST). SARDINES lives in the EXTRAS set — its slot 06
 * predates the numbering and collides with the book's 006 (The
 * Minister's Cat); renumber when Jordan finalizes the map.
 */
const FILLED: PpgGame[] = [CATEGORIES, STREGA_COMANDA_COLORE, SARDINES, HINKY_PINKY, GHOST]

export const PPG_GAMES: PpgGame[] = Array.from({ length: 50 }, (_, i) => {
  const number = i + 1
  return FILLED.find(g => g.number === number) ?? { number, title: null }
})

export function getPpgGame(gameNumber: number): PpgGame | undefined {
  return PPG_GAMES.find(g => g.number === gameNumber)
}
